import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Diamond, CheckCircle, Pencil, Trash2, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";

const STATUSES = ["Upcoming", "In Progress", "Awaiting Client", "On Hold", "Complete"];
const TASK_STATUSES = ["open", "in_progress", "blocked", "completed"];

function tone(s) {
    if (s === "Complete" || s === "completed") return "bg-[#EBE8E0] text-charcoal";
    if (s === "In Progress" || s === "in_progress") return "bg-charcoal text-white";
    if (s === "Awaiting Client") return "bg-[#FDF4F2] text-terracotta";
    if (s === "On Hold" || s === "blocked") return "bg-[#F5EFE7] text-[#7A6A50]";
    return "bg-white text-[#666] border border-[#E5E2DC]";
}

function fmtDate(v) {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function BackLink({ to, children }) {
    const navigate = useNavigate();
    return (
        <button onClick={() => navigate(to)} className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal" data-testid="b3b-back">
            <ArrowLeft className="w-4 h-4" /> {children}
        </button>
    );
}

// ============================================================
// Programme (List + Gantt shared records)
// ============================================================
export function ProjectProgramme() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [view, setView] = useState("list");
    const [data, setData] = useState({ items: [], summary: {} });
    const [minis, setMinis] = useState([]);
    const [filter, setFilter] = useState({ status: "", ws: "", mini: "" });
    const [showCreate, setShowCreate] = useState(false);
    usePageMeta({ title: "Programme · Project | Studio G4 Admin" });

    const load = async () => {
        const params = new URLSearchParams();
        if (filter.status) params.set("status", filter.status);
        if (filter.ws) params.set("linked_workstage", filter.ws);
        if (filter.mini) params.set("mini_programme_id", filter.mini);
        const [p, m] = await Promise.all([
            api.get(`/admin/projects/${id}/programme?${params}`),
            api.get(`/admin/projects/${id}/mini_programmes`),
        ]);
        setData(p.data);
        setMinis(m.data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, filter.status, filter.ws, filter.mini]);

    return (
        <div className="flex flex-col gap-4" data-testid="programme-page">
            <BackLink to={`/admin/projects/${id}`}>Back to workspace</BackLink>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">Programme</h1>
                <div className="flex items-center gap-2">
                    <div className="inline-flex border border-[#E5E2DC] rounded-md overflow-hidden" data-testid="programme-view-toggle">
                        <button onClick={() => setView("list")} data-testid="programme-view-list" data-active={view === "list"} className={`px-3 py-1.5 text-[13px] ${view === "list" ? "bg-charcoal text-white" : "bg-white text-charcoal"}`}>List</button>
                        <button onClick={() => setView("gantt")} data-testid="programme-view-gantt" data-active={view === "gantt"} className={`px-3 py-1.5 text-[13px] ${view === "gantt" ? "bg-charcoal text-white" : "bg-white text-charcoal"}`}>Gantt</button>
                    </div>
                    <button onClick={() => navigate(`/admin/projects/${id}/mini-programmes`)} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md hover:bg-[#FAFAFA]" data-testid="programme-view-minis">Mini Programmes</button>
                    <button onClick={() => setShowCreate(true)} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md flex items-center gap-1" data-testid="programme-create-open"><Plus className="w-3.5 h-3.5" />Add item</button>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 bg-white border border-[#E5E2DC] rounded-lg p-3" data-testid="programme-summary">
                {Object.entries(data.summary || {}).map(([k, v]) => (
                    <div key={k} className="text-[13px]" data-testid={`programme-summary-${k}`}>
                        <span className="text-[#666] uppercase tracking-wide font-manrope text-[11px] font-semibold mr-1">{k}</span>
                        <span className="font-semibold text-charcoal">{v}</span>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <select data-testid="programme-filter-status" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} className="text-[13px] px-2 py-1 border border-[#E5E2DC] rounded-md">
                    <option value="">All statuses</option>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="programme-filter-ws" value={filter.ws} onChange={e => setFilter(f => ({ ...f, ws: e.target.value }))} className="text-[13px] px-2 py-1 border border-[#E5E2DC] rounded-md">
                    <option value="">All Workstages</option>
                    {[1,2,3,4,5,6,7].map(w => <option key={w} value={w}>WS {w}</option>)}
                </select>
                <select data-testid="programme-filter-mini" value={filter.mini} onChange={e => setFilter(f => ({ ...f, mini: e.target.value }))} className="text-[13px] px-2 py-1 border border-[#E5E2DC] rounded-md">
                    <option value="">All Mini Programmes</option>
                    {minis.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
            </div>

            {view === "list" ? (
                <ProgrammeList items={data.items} onSelect={(it) => navigate(`/admin/projects/${id}/programme/${it.id}`)} />
            ) : (
                <ProgrammeGantt items={data.items} onSelect={(it) => navigate(`/admin/projects/${id}/programme/${it.id}`)} />
            )}

            {showCreate && <CreateItemModal onClose={() => setShowCreate(false)} onCreate={async (body) => { await api.post(`/admin/projects/${id}/programme`, body); setShowCreate(false); load(); }} minis={minis} />}
        </div>
    );
}

function ProgrammeList({ items, onSelect }) {
    if (!items || items.length === 0) return <div className="text-[#666] text-[13px]" data-testid="programme-list-empty">No programme items yet.</div>;
    return (
        <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto" data-testid="programme-list">
            <table className="w-full text-[13px] text-left">
                <thead className="text-[#666] font-normal text-[12px]">
                    <tr>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Title</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Type</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">WS</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Status</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Owner</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Planned start</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Planned end</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Due</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Client</th>
                        <th className="py-3 px-3 border-b border-[#E5E2DC]">Overdue</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(it => (
                        <tr key={it.id} onClick={() => onSelect(it)} className="cursor-pointer hover:bg-[#FAFAFA] border-t border-[#E5E2DC]" data-testid={`programme-item-row-${it.id}`}>
                            <td className="py-2.5 px-3 font-medium text-charcoal">{it.item_type === "Milestone" && <Diamond className="w-3 h-3 inline mr-1" />}{it.title}</td>
                            <td className="py-2.5 px-3 text-[#666]">{it.item_type}</td>
                            <td className="py-2.5 px-3">{it.linked_workstage ?? "—"}</td>
                            <td className="py-2.5 px-3"><span className={`px-2 py-0.5 rounded text-[11px] font-manrope font-semibold uppercase tracking-wide ${tone(it.status)}`}>{it.status}</span></td>
                            <td className="py-2.5 px-3 text-[#666]">{it.owner_id || "—"}</td>
                            <td className="py-2.5 px-3">{fmtDate(it.planned_start_at)}</td>
                            <td className="py-2.5 px-3">{fmtDate(it.planned_end_at)}</td>
                            <td className="py-2.5 px-3">{fmtDate(it.due_at)}</td>
                            <td className="py-2.5 px-3 text-[#666]">{it.client_visible ? "Yes" : "No"}</td>
                            <td className="py-2.5 px-3 text-terracotta">{it.overdue ? "Yes" : ""}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ProgrammeGantt({ items, onSelect }) {
    const rows = useMemo(() => items.filter(it => it.planned_start_at || it.planned_end_at || it.due_at), [items]);
    const { min, max, days } = useMemo(() => {
        if (rows.length === 0) return { min: null, max: null, days: 0 };
        let lo = Infinity, hi = -Infinity;
        for (const it of rows) {
            for (const k of ["planned_start_at", "planned_end_at", "due_at"]) {
                if (it[k]) {
                    const t = new Date(it[k]).getTime();
                    if (t < lo) lo = t;
                    if (t > hi) hi = t;
                }
            }
        }
        if (!isFinite(lo) || !isFinite(hi) || lo === hi) { hi = lo + 30 * 86400000; }
        return { min: lo, max: hi, days: Math.max(1, Math.ceil((hi - lo) / 86400000)) };
    }, [rows]);
    if (rows.length === 0) return <div className="text-[#666] text-[13px]" data-testid="programme-gantt-empty">No dated programme items to plot.</div>;
    const pxPerDay = 26;
    const width = days * pxPerDay + 220;
    const barY = (t) => 16;
    return (
        <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto" data-testid="programme-gantt">
            <div style={{ width }} className="relative">
                <div className="flex items-center gap-0 border-b border-[#E5E2DC] sticky top-0 bg-white z-10">
                    <div style={{ width: 220 }} className="py-2 px-3 text-[11px] uppercase tracking-wide font-manrope font-semibold text-[#666] border-r border-[#E5E2DC]">Item</div>
                    <div className="flex-1 relative h-8">
                        {Array.from({ length: days + 1 }, (_, i) => (
                            <div key={i} style={{ left: i * pxPerDay }} className="absolute top-0 h-full border-l border-[#F0EDE7] text-[10px] text-[#999] pl-1">
                                {i % 7 === 0 && new Date(min + i * 86400000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            </div>
                        ))}
                    </div>
                </div>
                {rows.map((it) => {
                    const s = it.planned_start_at ? new Date(it.planned_start_at).getTime() : null;
                    const e = it.planned_end_at ? new Date(it.planned_end_at).getTime() : (it.due_at ? new Date(it.due_at).getTime() : s);
                    const leftDays = s ? Math.max(0, Math.floor((s - min) / 86400000)) : 0;
                    const spanDays = s && e ? Math.max(1, Math.ceil((e - s) / 86400000) || 1) : 1;
                    const isMs = it.item_type === "Milestone";
                    return (
                        <div key={it.id} onClick={() => onSelect(it)} className="flex items-center gap-0 border-b border-[#F0EDE7] cursor-pointer hover:bg-[#FAFAFA]" data-testid={`programme-gantt-row-${it.id}`}>
                            <div style={{ width: 220 }} className="py-2 px-3 text-[13px] border-r border-[#E5E2DC] truncate">
                                {isMs && <Diamond className="w-3 h-3 inline mr-1" />}{it.title}
                            </div>
                            <div className="flex-1 relative h-10">
                                {isMs ? (
                                    <div style={{ left: leftDays * pxPerDay - 6, top: barY() - 4 }} className="absolute w-3 h-3 rotate-45 bg-charcoal" data-testid={`gantt-milestone-${it.id}`} />
                                ) : (
                                    <div style={{ left: leftDays * pxPerDay, width: spanDays * pxPerDay, top: barY() - 6 }} className={`absolute rounded ${tone(it.status)} px-1.5 py-0.5 text-[11px] font-medium overflow-hidden whitespace-nowrap`} data-testid={`gantt-bar-${it.id}`}>
                                        {it.title}
                                    </div>
                                )}
                                {it.client_visible && <span className="absolute right-1 top-1 text-[9px] uppercase tracking-wide text-terracotta">CV</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CreateItemModal({ onClose, onCreate, minis }) {
    const [form, setForm] = useState({ title: "", item_type: "Task", linked_workstage: "", planned_start_at: "", planned_end_at: "", due_at: "", client_visible: false, description: "", mini_programme_id: "" });
    const submit = async () => {
        await onCreate({
            ...form,
            linked_workstage: form.linked_workstage ? parseInt(form.linked_workstage) : null,
            mini_programme_id: form.mini_programme_id || null,
        });
    };
    return (
        <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" data-testid="programme-create-modal">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <div className="w-[420px] bg-white h-full overflow-y-auto p-4 flex flex-col gap-3">
                <h2 className="font-manrope font-semibold text-charcoal text-[16px]">Add programme item</h2>
                <label className="flex flex-col gap-1 text-[12px]">Title
                    <input data-testid="programme-create-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Type
                    <select data-testid="programme-create-type" value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]">
                        <option value="Task">Task</option>
                        <option value="Milestone">Milestone</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Linked Workstage
                    <select value={form.linked_workstage} onChange={e => setForm(f => ({ ...f, linked_workstage: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]">
                        <option value="">—</option>
                        {[1,2,3,4,5,6,7].map(w => <option key={w} value={w}>WS {w}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Planned start
                    <input type="date" value={form.planned_start_at} onChange={e => setForm(f => ({ ...f, planned_start_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Planned end
                    <input type="date" value={form.planned_end_at} onChange={e => setForm(f => ({ ...f, planned_end_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Due
                    <input type="date" value={form.due_at} onChange={e => setForm(f => ({ ...f, due_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">Mini Programme
                    <select value={form.mini_programme_id} onChange={e => setForm(f => ({ ...f, mini_programme_id: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]">
                        <option value="">—</option>
                        {minis.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                </label>
                <label className="inline-flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={form.client_visible} onChange={e => setForm(f => ({ ...f, client_visible: e.target.checked }))} data-testid="programme-create-client-visible" /> Client visible
                </label>
                <div className="flex items-center gap-2 mt-2">
                    <button onClick={submit} data-testid="programme-create-submit" className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">Create</button>
                    <button onClick={onClose} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// Programme item detail — reschedule / status / owner
// ============================================================
export function ProgrammeItemDetail() {
    const { id, itemId } = useParams();
    const [data, setData] = useState(null);
    const [reschedule, setReschedule] = useState(null);
    const [statusForm, setStatusForm] = useState(null);
    const itemLabel = data?.item?.title || "Programme item";
    usePageMeta({ title: `${itemLabel} · Programme | Studio G4 Admin` });

    const load = async () => {
        const r = await api.get(`/admin/projects/${id}/programme/${itemId}`);
        setData(r.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, itemId]);
    if (!data) return <div>Loading…</div>;
    const it = data.item;

    const doStatus = async () => {
        try {
            await api.post(`/admin/programme/${itemId}/status`, { expected_version: it.record_version, status: statusForm.status, reason: statusForm.reason });
            setStatusForm(null); load();
        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    };
    const doComplete = async () => {
        try {
            await api.post(`/admin/programme/${itemId}/complete`, { expected_version: it.record_version });
            load();
        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    };
    const doReschedule = async () => {
        const supplied = [];
        for (const k of ["planned_start_at", "planned_end_at", "due_at"])
            if (reschedule[k] !== "" && reschedule[k] !== undefined) supplied.push(k);
        try {
            await api.post(`/admin/programme/${itemId}/reschedule`, {
                expected_version: it.record_version,
                planned_start_at: reschedule.planned_start_at || null,
                planned_end_at: reschedule.planned_end_at || null,
                due_at: reschedule.due_at || null,
                reason: reschedule.reason,
                supplied,
            });
            setReschedule(null); load();
        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="flex flex-col gap-4" data-testid="programme-item-detail">
            <BackLink to={`/admin/projects/${id}/programme`}>Back to programme</BackLink>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">{it.title}</h1>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                <div className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2 text-[13px]">
                    <div><span className="text-[#666]">Type:</span> {it.item_type}</div>
                    <div><span className="text-[#666]">Status:</span> <span className={`px-2 py-0.5 rounded text-[11px] font-manrope font-semibold uppercase tracking-wide ${tone(it.status)}`}>{it.status}</span></div>
                    <div><span className="text-[#666]">Linked Workstage:</span> {it.linked_workstage ?? "—"}</div>
                    <div><span className="text-[#666]">Owner:</span> {it.owner_id || "—"}</div>
                    <div><span className="text-[#666]">Planned start:</span> {fmtDate(it.planned_start_at)}</div>
                    <div><span className="text-[#666]">Planned end:</span> {fmtDate(it.planned_end_at)}</div>
                    <div><span className="text-[#666]">Due:</span> {fmtDate(it.due_at)}</div>
                    <div><span className="text-[#666]">Client visible:</span> {it.client_visible ? "Yes" : "No"}</div>
                    <div><span className="text-[#666]">Overdue:</span> {it.overdue ? "Yes" : "No"} <span className="text-[10px] text-[#999] ml-1">(derived)</span></div>
                </div>
                <div className="bg-white border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2">
                    <div className="font-manrope text-[11px] uppercase tracking-wide text-[#666] font-semibold">Actions</div>
                    <button onClick={() => setStatusForm({ status: STATUSES[0], reason: "" })} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-left hover:bg-[#FAFAFA]" data-testid="pi-action-status">Change status</button>
                    <button onClick={() => setReschedule({ planned_start_at: "", planned_end_at: "", due_at: "", reason: "" })} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-left hover:bg-[#FAFAFA]" data-testid="pi-action-reschedule">Reschedule…</button>
                    <button onClick={doComplete} disabled={it.status === "Complete"} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-left hover:bg-[#FAFAFA] disabled:opacity-40" data-testid="pi-action-complete"><CheckCircle className="w-3 h-3 inline mr-1" />Mark complete</button>
                </div>
            </div>

            <div className="bg-white border border-[#E5E2DC] rounded-lg p-4" data-testid="programme-item-history">
                <div className="font-manrope text-[11px] uppercase tracking-wide text-[#666] font-semibold mb-2">Reschedule history</div>
                {(!data.reschedule_history || data.reschedule_history.length === 0) ? (
                    <div className="text-[12.5px] text-[#999]">No reschedules yet.</div>
                ) : (
                    <ul className="flex flex-col gap-2">
                        {data.reschedule_history.map(h => (
                            <li key={h.id} className="border border-[#E5E2DC] rounded-md p-2 text-[12.5px]" data-testid={`programme-history-${h.id}`}>
                                <div><span className="text-[#666]">Reason:</span> {h.reason || "—"}</div>
                                <div>Previous: start {fmtDate(h.previous.planned_start_at)} · end {fmtDate(h.previous.planned_end_at)} · due {fmtDate(h.previous.due_at)}</div>
                                <div>New: start {fmtDate(h.next.planned_start_at)} · end {fmtDate(h.next.planned_end_at)} · due {fmtDate(h.next.due_at)}</div>
                                <div className="text-[11px] text-[#999]">{h.actor} · {fmtDate(h.created_at)}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {statusForm && (
                <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center" role="dialog" aria-modal="true" data-testid="pi-status-modal">
                    <div className="bg-white rounded-lg p-4 w-[380px] flex flex-col gap-3">
                        <h3 className="font-manrope font-semibold">Change status</h3>
                        <select value={statusForm.status} onChange={e => setStatusForm(s => ({ ...s, status: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]">
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <input placeholder="Reason (optional)" value={statusForm.reason} onChange={e => setStatusForm(s => ({ ...s, reason: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        <div className="flex gap-2">
                            <button onClick={doStatus} data-testid="pi-status-submit" className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">Apply</button>
                            <button onClick={() => setStatusForm(null)} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {reschedule && (
                <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center" role="dialog" aria-modal="true" data-testid="pi-reschedule-modal">
                    <div className="bg-white rounded-lg p-4 w-[420px] flex flex-col gap-3">
                        <h3 className="font-manrope font-semibold">Reschedule item</h3>
                        <p className="text-[12px] text-[#666]">Proposed new dates. Nothing is saved until you confirm.</p>
                        <label className="text-[12px] flex flex-col gap-1">Planned start (was {fmtDate(it.planned_start_at)})
                            <input type="date" value={reschedule.planned_start_at} onChange={e => setReschedule(r => ({ ...r, planned_start_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        </label>
                        <label className="text-[12px] flex flex-col gap-1">Planned end (was {fmtDate(it.planned_end_at)})
                            <input type="date" value={reschedule.planned_end_at} onChange={e => setReschedule(r => ({ ...r, planned_end_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        </label>
                        <label className="text-[12px] flex flex-col gap-1">Due (was {fmtDate(it.due_at)})
                            <input type="date" value={reschedule.due_at} onChange={e => setReschedule(r => ({ ...r, due_at: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" />
                        </label>
                        <label className="text-[12px] flex flex-col gap-1">Reason (required)
                            <input value={reschedule.reason} onChange={e => setReschedule(r => ({ ...r, reason: e.target.value }))} className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" data-testid="pi-reschedule-reason" />
                        </label>
                        <div className="flex gap-2">
                            <button onClick={doReschedule} disabled={!reschedule.reason} data-testid="pi-reschedule-submit" className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">Confirm reschedule</button>
                            <button onClick={() => setReschedule(null)} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================
// Mini Programmes
// ============================================================
export function ProjectMiniProgrammes() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [show, setShow] = useState(false);
    const [name, setName] = useState("");
    usePageMeta({ title: "Mini programmes · Project | Studio G4 Admin" });
    const load = async () => {
        const r = await api.get(`/admin/projects/${id}/mini_programmes`);
        setItems(r.data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    return (
        <div className="flex flex-col gap-4" data-testid="mini-programmes-page">
            <BackLink to={`/admin/projects/${id}/programme`}>Back to programme</BackLink>
            <div className="flex items-center justify-between">
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">Mini Programmes</h1>
                <button onClick={() => setShow(true)} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md flex items-center gap-1" data-testid="mini-create-open"><Plus className="w-3.5 h-3.5" />New</button>
            </div>
            <p className="text-[12.5px] text-[#666]">Mini Programmes are one-level groupings of the same global Programme Items — no duplicated dates, no separate scheduling engine.</p>
            {items.length === 0 ? (
                <div className="text-[#666] text-[13px]" data-testid="minis-empty">No Mini Programmes yet.</div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {items.map(m => (
                        <li key={m.id} onClick={() => navigate(`/admin/projects/${id}/mini-programmes/${m.id}`)} className="bg-white border border-[#E5E2DC] rounded-md p-3 cursor-pointer hover:bg-[#FAFAFA]" data-testid={`mini-row-${m.id}`}>
                            <div className="font-manrope font-semibold text-charcoal">{m.name}</div>
                            <div className="text-[12px] text-[#666]">{m.item_count} items</div>
                        </li>
                    ))}
                </ul>
            )}
            {show && (
                <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center" role="dialog" aria-modal="true" data-testid="mini-create-modal">
                    <div className="bg-white rounded-lg p-4 w-[380px] flex flex-col gap-3">
                        <h3 className="font-manrope font-semibold">New Mini Programme</h3>
                        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px]" data-testid="mini-create-name" />
                        <div className="flex gap-2">
                            <button onClick={async () => { if (name.trim()) { await api.post(`/admin/projects/${id}/mini_programmes`, { name }); setName(""); setShow(false); load(); } }} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md" data-testid="mini-create-submit">Create</button>
                            <button onClick={() => setShow(false)} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function MiniProgrammeDetail() {
    const { id, mid } = useParams();
    const [data, setData] = useState(null);
    const miniLabel = data?.item?.name || "Mini programme";
    usePageMeta({ title: `${miniLabel} · Mini programme | Studio G4 Admin` });
    useEffect(() => { api.get(`/admin/projects/${id}/mini_programmes/${mid}`).then(r => setData(r.data)); }, [id, mid]);
    if (!data) return <div>Loading…</div>;
    return (
        <div className="flex flex-col gap-4" data-testid="mini-detail-page">
            <BackLink to={`/admin/projects/${id}/mini-programmes`}>Back to Mini Programmes</BackLink>
            <h1 className="font-manrope text-2xl font-semibold text-charcoal">{data.item.name}</h1>
            <p className="text-[12.5px] text-[#666]">Included Programme Items are references to the global Programme — dates are authoritative there.</p>
            <ProgrammeList items={data.programme_items} onSelect={(it) => window.location.href = `/admin/projects/${id}/programme/${it.id}`} />
        </div>
    );
}

// ============================================================
// Internal Tasks
// ============================================================

// Canonical 7-workstage list — mirrors `stage5_b1_projects.WORKSTAGES`
// (backend authority). Duplicated per repo convention (see
// ProjectsLanding.jsx:22 and ProjectWorkspace.jsx:45).
const TASK_WORKSTAGES = [
    { num: 1, name: "Initial Enquiry + Brief Development" },
    { num: 2, name: "Project Feasibility" },
    { num: 3, name: "Concept Design" },
    { num: 4, name: "Developed + Technical Design" },
    { num: 5, name: "Pre-Construction" },
    { num: 6, name: "Construction" },
    { num: 7, name: "Post-Construction" },
];

// Owner label with the same fallback chain used by ProjectsLanding.jsx
// OwnerCell (line 123–124): display_name → 8-char id prefix → em dash.
function ownerLabel(t) {
    if (t.owner_display_name) return t.owner_display_name;
    if (t.owner_id) return `${t.owner_id.slice(0, 8)}…`;
    return "—";
}

// Pass 1 §Q1 — a task is "in a past workstage" when the project has
// moved on to a later workstage than the one the task is anchored to.
// The backend already refuses mutations in this state (see
// stage5_tasks._require_task_in_current_or_future_ws); the frontend
// disables the buttons pre-emptively for honesty.
function isPastWorkstageTask(t, projectCurrentWorkstage) {
    if (t.linked_workstage == null) return false;
    if (!projectCurrentWorkstage) return false;
    return t.linked_workstage < projectCurrentWorkstage;
}
const PAST_WS_TOOLTIP = "This task is in a completed workstage.";

// Human label for a workstage integer using the canonical constant.
function workstageLabel(n) {
    const w = TASK_WORKSTAGES.find((x) => x.num === n);
    return w ? `WS ${w.num} · ${w.name}` : (n ? `WS ${n}` : "—");
}

// Sort key within a workstage group: completed rows sink to the bottom,
// then soonest due_at first, then undated to the bottom of open rows.
function taskSortKey(t) {
    const completed = t.is_completed ? 1 : 0;
    const noDue = t.due_at ? 0 : 1;
    const due = t.due_at ? new Date(t.due_at).getTime() : Number.MAX_SAFE_INTEGER;
    return [completed, noDue, due];
}
function compareTasks(a, b) {
    const ka = taskSortKey(a);
    const kb = taskSortKey(b);
    for (let i = 0; i < ka.length; i++) {
        if (ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1;
    }
    return 0;
}

export function ProjectTasks() {
    const { id } = useParams();
    const [data, setData] = useState({ items: [], summary: {},
                                        project_current_workstage: null });
    const [drafts, setDrafts] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [createUnassignedOpen, setCreateUnassignedOpen] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [deleteTask, setDeleteTask] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [deleteDraft, setDeleteDraft] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    usePageMeta({ title: "Tasks · Project | Studio G4 Admin" });

    const load = async () => {
        const r = await api.get(`/admin/projects/${id}/tasks`);
        setData(r.data);
    };
    const loadDrafts = async () => {
        try {
            const r = await api.get(`/admin/projects/${id}/unassigned-tasks`);
            setDrafts(r.data?.items || []);
        } catch { setDrafts([]); }
    };
    useEffect(() => { load(); loadDrafts(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        let cancelled = false;
        api.get("/admin/users").then((r) => {
            if (cancelled) return;
            setAdmins((r.data?.items || []).filter((u) => u.is_active !== false));
        }).catch(() => setAdmins([]));
        return () => { cancelled = true; };
    }, []);
    // Close the split-button menu on outside click.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [menuOpen]);

    const currentWs = data.project_current_workstage || null;

    const grouped = useMemo(() => {
        const buckets = new Map(TASK_WORKSTAGES.map((w) => [w.num, []]));
        const unassigned = [];
        for (const t of data.items || []) {
            const ws = t.linked_workstage;
            if (buckets.has(ws)) buckets.get(ws).push(t);
            else unassigned.push(t);
        }
        for (const arr of buckets.values()) arr.sort(compareTasks);
        unassigned.sort(compareTasks);
        return { buckets, unassigned };
    }, [data.items]);

    const onComplete = async (t) => {
        try {
            await api.post(`/admin/tasks/${t.id}/complete`,
                { expected_version: t.record_version });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not complete task");
        }
    };

    const onAssignDraft = async (d) => {
        try {
            await api.post(`/admin/unassigned-tasks/${d.id}/assign`);
            toast.success(currentWs
                ? `Assigned to WS ${currentWs}`
                : "Draft promoted");
            await Promise.all([load(), loadDrafts()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not assign draft");
        }
    };

    return (
        <div className="flex flex-col gap-4" data-testid="tasks-page">
            <BackLink to={`/admin/projects/${id}`}>Back to workspace</BackLink>
            <div className="flex items-center justify-between">
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">Internal Tasks</h1>
                <div className="relative" ref={menuRef}>
                    <div className="flex items-stretch">
                        <button onClick={() => { setMenuOpen(false); setCreateOpen(true); }}
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-l-md flex items-center gap-1"
                                data-testid="task-create-open">
                            <Plus className="w-3.5 h-3.5" />New task
                        </button>
                        <button onClick={() => setMenuOpen((v) => !v)}
                                aria-label="More task options"
                                className="text-[13px] px-2 py-1.5 bg-charcoal text-white rounded-r-md border-l border-white/20 flex items-center"
                                data-testid="task-create-menu">
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    {menuOpen && (
                        <div className="absolute right-0 mt-1 bg-white border border-[#E5E2DC] rounded-md shadow-lg z-30 min-w-[220px]"
                             data-testid="task-create-menu-panel">
                            <button onClick={() => { setMenuOpen(false); setCreateOpen(true); }}
                                    className="w-full text-left text-[13px] px-3 py-2 hover:bg-[#FAFAFA] text-charcoal"
                                    data-testid="task-create-menu-current">
                                Task in current workstage
                            </button>
                            <button onClick={() => { setMenuOpen(false); setCreateUnassignedOpen(true); }}
                                    className="w-full text-left text-[13px] px-3 py-2 hover:bg-[#FAFAFA] text-charcoal border-t border-[#E5E2DC]"
                                    data-testid="task-create-menu-unassigned">
                                Unassigned task
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap gap-3 bg-white border border-[#E5E2DC] rounded-lg p-3" data-testid="tasks-summary">
                {Object.entries(data.summary || {}).map(([k, v]) => (
                    <div key={k} className="text-[13px]" data-testid={`tasks-summary-${k}`}>
                        <span className="text-[#666] uppercase tracking-wide font-manrope text-[11px] font-semibold mr-1">{k}</span>
                        <span className="font-semibold text-charcoal">{v}</span>
                    </div>
                ))}
            </div>
            {/* Unassigned Tasks section — pending decisions surface at top. */}
            <UnassignedTasksSection
                drafts={drafts}
                currentWs={currentWs}
                onAssign={onAssignDraft}
                onEdit={setEditDraft}
                onDelete={setDeleteDraft} />
            <div className="flex flex-col gap-5" data-testid="tasks-workstage-sections">
                {TASK_WORKSTAGES.map(({ num, name }) => {
                    const list = grouped.buckets.get(num) || [];
                    const openCount = list.filter((t) => !t.is_completed).length;
                    return (
                        <TaskGroupSection key={num} testid={`tasks-ws-section-${num}`}
                                           heading={`WS ${num} · ${name}`}
                                           openCount={openCount}
                                           list={list}
                                           currentWs={currentWs}
                                           countTestid={`tasks-ws-count-${num}`}
                                           emptyTestid={`tasks-ws-empty-${num}`}
                                           onEdit={setEditTask}
                                           onDelete={setDeleteTask}
                                           onComplete={onComplete} />
                    );
                })}
                {grouped.unassigned.length > 0 && (
                    <TaskGroupSection testid="tasks-ws-section-unassigned"
                                       heading="No workstage linked"
                                       openCount={grouped.unassigned.filter((t) => !t.is_completed).length}
                                       list={grouped.unassigned}
                                       currentWs={currentWs}
                                       countTestid="tasks-ws-count-unassigned"
                                       emptyTestid="tasks-ws-empty-unassigned"
                                       onEdit={setEditTask}
                                       onDelete={setDeleteTask}
                                       onComplete={onComplete} />
                )}
            </div>
            {createOpen && (
                <CreateTaskModal
                    projectId={id}
                    admins={admins}
                    currentWs={currentWs}
                    onClose={() => setCreateOpen(false)}
                    onSaved={load} />
            )}
            {createUnassignedOpen && (
                <CreateUnassignedTaskModal
                    projectId={id}
                    admins={admins}
                    onClose={() => setCreateUnassignedOpen(false)}
                    onSaved={loadDrafts} />
            )}
            {editTask && (
                <EditTaskModal
                    task={editTask}
                    admins={admins}
                    onClose={() => setEditTask(null)}
                    onSaved={load} />
            )}
            {editDraft && (
                <EditUnassignedTaskModal
                    draft={editDraft}
                    admins={admins}
                    onClose={() => setEditDraft(null)}
                    onSaved={loadDrafts} />
            )}
            {deleteTask && (
                <DeleteTaskModal
                    task={deleteTask}
                    onClose={() => setDeleteTask(null)}
                    onDeleted={load} />
            )}
            {deleteDraft && (
                <DeleteUnassignedTaskModal
                    draft={deleteDraft}
                    onClose={() => setDeleteDraft(null)}
                    onDeleted={loadDrafts} />
            )}
        </div>
    );
}

// Shared shell for Create + Edit — mirrors the existing custom-div modal
// pattern used elsewhere in this file (pi-status-modal, pi-reschedule-modal,
// mini-create-modal). Feasibility's LrsModal is intentionally NOT imported
// (feasibility code is off-limits per Strategy guardrail).
function TaskGroupSection({ testid, heading, openCount, list, currentWs,
                            countTestid, emptyTestid,
                            onEdit, onDelete, onComplete }) {
    return (
        <section data-testid={testid} className="flex flex-col gap-2">
            <header className="flex items-center gap-3 px-3 py-2 bg-[#EBE8E0] rounded-md">
                <span className="font-manrope font-semibold text-charcoal text-[14px]">
                    {heading}
                </span>
                <span className="text-[12px] text-[#666]" data-testid={countTestid}>
                    {openCount} open
                </span>
            </header>
            {list.length === 0 ? (
                <div className="text-[#999] italic text-[13px] px-3 py-2"
                     data-testid={emptyTestid}>
                    No tasks in this stage.
                </div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {list.map((t) => {
                        const pastWs = isPastWorkstageTask(t, currentWs);
                        const disabledTip = pastWs ? PAST_WS_TOOLTIP : undefined;
                        return (
                        <li key={t.id}
                            className={`bg-white border border-[#E5E2DC] rounded-md p-3 ${t.is_completed ? "opacity-60" : ""}`}
                            data-testid={`task-row-${t.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex flex-col min-w-0">
                                    <div className={`font-manrope font-semibold text-charcoal ${t.is_completed ? "line-through" : ""}`}>
                                        {t.title}
                                    </div>
                                    <div className="text-[12px] text-[#666]"
                                         data-testid={`task-meta-${t.id}`}>
                                        Owner: <span data-testid={`task-owner-${t.id}`}>{ownerLabel(t)}</span>
                                        {" · Due: "}{fmtDate(t.due_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {t.overdue && !t.is_completed && (
                                        <span className="text-[11px] text-terracotta font-semibold">Overdue</span>
                                    )}
                                    {t.is_completed ? (
                                        <span className="px-2 py-0.5 rounded text-[11px] font-manrope font-semibold uppercase tracking-wide bg-[#EBE8E0] text-charcoal">
                                            Completed
                                        </span>
                                    ) : (
                                        <button onClick={() => onComplete(t)}
                                                disabled={pastWs}
                                                title={disabledTip}
                                                data-testid={`task-complete-${t.id}`}
                                                className="text-[12px] px-2 py-0.5 border border-[#E5E2DC] rounded hover:bg-[#FAFAFA] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                            Complete
                                        </button>
                                    )}
                                    <button onClick={() => onEdit(t)}
                                            disabled={pastWs}
                                            title={disabledTip}
                                            data-testid={`task-edit-${t.id}`}
                                            className="text-[12px] px-2 py-0.5 border border-[#E5E2DC] rounded hover:bg-[#FAFAFA] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                                        <Pencil className="w-3 h-3" />Edit
                                    </button>
                                    <button onClick={() => onDelete(t)}
                                            disabled={pastWs}
                                            title={disabledTip}
                                            data-testid={`task-delete-${t.id}`}
                                            className="text-[12px] px-2 py-0.5 border border-[#E5E2DC] rounded hover:bg-[#FDF4F2] hover:text-terracotta inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-inherit">
                                        <Trash2 className="w-3 h-3" />Delete
                                    </button>
                                </div>
                            </div>
                        </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

// Pass 1 §Q3 correction — separate Create and Edit modals.  The prior
// shared modal caused a regression where Edit-mode invariants leaked
// into Create-mode (workstage rendered read-only when the user should
// see the project's honest current workstage).  Keep them separate.

function CreateTaskModal({ projectId, admins, currentWs, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: "",
        description: "",
        owner_id: "",
        due_at: "",
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/projects/${projectId}/tasks`, {
                title: form.title,
                description: form.description || null,
                owner_id: form.owner_id || null,
                due_at: form.due_at || null,
            });
            toast.success("Task created");
            onClose();
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create task");
        } finally {
            setSaving(false);
        }
    };
    const testid = "task-create-modal";
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid={testid}>
            <div className="bg-white rounded-lg p-4 w-[440px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">New task</h3>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Title
                    <input value={form.title}
                           onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-title`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Description
                    <textarea value={form.description} rows={3}
                              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                              className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                              data-testid={`${testid}-description`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Workstage
                    <input
                        readOnly
                        value={currentWs ? workstageLabel(currentWs) : "—"}
                        title="New tasks are anchored to the project's currently active workstage."
                        className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-[#666] bg-[#FAFAFA] cursor-not-allowed"
                        data-testid={`${testid}-workstage-current`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Owner
                    <select value={form.owner_id}
                            onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                            className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                            data-testid={`${testid}-owner`}>
                        <option value="">Unassigned</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.display_name || a.email}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Due date
                    <input type="date" value={form.due_at}
                           onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-due`} />
                </label>
                <div className="flex gap-2 justify-end mt-1">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid={`${testid}-cancel`}>Cancel</button>
                    <button onClick={save} disabled={saving}
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-50"
                            data-testid={`${testid}-submit`}>
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EditTaskModal({ task, admins, onClose, onSaved }) {
    const toDateInput = (iso) => {
        if (!iso) return "";
        try { return new Date(iso).toISOString().slice(0, 10); }
        catch { return ""; }
    };
    const [form, setForm] = useState({
        title: task?.title || "",
        description: task?.description || "",
        owner_id: task?.owner_id || "",
        due_at: toDateInput(task?.due_at),
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/tasks/${task.id}`, {
                expected_version: task.record_version,
                title: form.title,
                description: form.description || null,
                owner_id: form.owner_id || null,
                due_at: form.due_at || null,
            });
            toast.success("Task updated");
            onClose();
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save task");
        } finally {
            setSaving(false);
        }
    };
    const testid = "task-edit-modal";
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid={testid}>
            <div className="bg-white rounded-lg p-4 w-[440px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">Edit task</h3>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Title
                    <input value={form.title}
                           onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-title`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Description
                    <textarea value={form.description} rows={3}
                              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                              className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                              data-testid={`${testid}-description`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Workstage
                    <input
                        readOnly
                        value={workstageLabel(task.linked_workstage)}
                        title="Workstage is set at creation and cannot be changed."
                        className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-[#666] bg-[#FAFAFA] cursor-not-allowed"
                        data-testid={`${testid}-workstage-locked`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Owner
                    <select value={form.owner_id}
                            onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                            className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                            data-testid={`${testid}-owner`}>
                        <option value="">Unassigned</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.display_name || a.email}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Due date
                    <input type="date" value={form.due_at}
                           onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-due`} />
                </label>
                <div className="flex gap-2 justify-end mt-1">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid={`${testid}-cancel`}>Cancel</button>
                    <button onClick={save} disabled={saving}
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-50"
                            data-testid={`${testid}-submit`}>
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DeleteTaskModal({ task, onClose, onDeleted }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!reason.trim()) {
            toast.error("Please provide a reason.");
            return;
        }
        setBusy(true);
        try {
            await api.delete(`/admin/tasks/${task.id}`, {
                data: { expected_version: task.record_version,
                        reason: reason.trim() },
            });
            toast.success("Task deleted");
            onClose();
            onDeleted();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete task");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid="task-delete-modal">
            <div className="bg-white rounded-lg p-4 w-[400px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">Delete task</h3>
                <p className="text-[13px] text-[#666]">
                    This will soft-delete <span className="font-semibold text-charcoal">"{task.title}"</span>.
                    A reason is required for the audit trail.
                </p>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Reason
                    <textarea value={reason} rows={3}
                              onChange={(e) => setReason(e.target.value)}
                              className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                              data-testid="task-delete-reason" />
                </label>
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid="task-delete-cancel">Cancel</button>
                    <button onClick={submit} disabled={busy}
                            className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md disabled:opacity-50"
                            data-testid="task-delete-confirm">
                        {busy ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}


// ============================================================
// Phase B — Unassigned (draft) Tasks
// ============================================================

function UnassignedTasksSection({ drafts, currentWs, onAssign, onEdit, onDelete }) {
    const empty = drafts.length === 0;
    return (
        <section data-testid="unassigned-tasks-section" className="flex flex-col gap-2">
            <header className="flex items-center gap-3 px-3 py-2 bg-[#EBE8E0] rounded-md">
                <span className="font-manrope font-semibold text-charcoal text-[14px]">
                    Unassigned Tasks
                </span>
                <span className="text-[12px] text-[#666]" data-testid="unassigned-tasks-count">
                    {empty ? "No drafts" : `${drafts.length} pending`}
                </span>
            </header>
            {empty ? (
                <div className="text-[#999] italic text-[13px] px-3 py-2"
                     data-testid="unassigned-tasks-empty">
                    No unassigned tasks.
                </div>
            ) : (
                <ul className="flex flex-col gap-2">
                    {drafts.map((d) => (
                        <li key={d.id}
                            className="bg-white border border-[#E5E2DC] rounded-md p-3"
                            data-testid={`unassigned-task-row-${d.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex flex-col min-w-0">
                                    <div className="font-manrope font-semibold text-charcoal">
                                        {d.title}
                                    </div>
                                    <div className="text-[12px] text-[#666]"
                                         data-testid={`unassigned-task-meta-${d.id}`}>
                                        Owner: {d.owner_display_name
                                            || (d.owner_id ? `${d.owner_id.slice(0,8)}…` : "—")}
                                        {" · Due: "}{fmtDate(d.due_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => onAssign(d)}
                                            disabled={!currentWs}
                                            title={currentWs
                                                ? undefined
                                                : "Project has no Active workstage — cannot assign."}
                                            data-testid={`unassigned-task-assign-${d.id}`}
                                            className="text-[12px] px-2 py-0.5 bg-charcoal text-white rounded inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                                        <ArrowRight className="w-3 h-3" />
                                        {currentWs ? `Assign to WS ${currentWs}` : "Assign"}
                                    </button>
                                    <button onClick={() => onEdit(d)}
                                            data-testid={`unassigned-task-edit-${d.id}`}
                                            className="text-[12px] px-2 py-0.5 border border-[#E5E2DC] rounded hover:bg-[#FAFAFA] inline-flex items-center gap-1">
                                        <Pencil className="w-3 h-3" />Edit
                                    </button>
                                    <button onClick={() => onDelete(d)}
                                            data-testid={`unassigned-task-delete-${d.id}`}
                                            className="text-[12px] px-2 py-0.5 border border-[#E5E2DC] rounded hover:bg-[#FDF4F2] hover:text-terracotta inline-flex items-center gap-1">
                                        <Trash2 className="w-3 h-3" />Delete
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function CreateUnassignedTaskModal({ projectId, admins, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: "", description: "", owner_id: "", due_at: "",
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/projects/${projectId}/unassigned-tasks`, {
                title: form.title,
                description: form.description || null,
                owner_id: form.owner_id || null,
                due_at: form.due_at || null,
            });
            toast.success("Unassigned task created");
            onClose();
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not create unassigned task");
        } finally {
            setSaving(false);
        }
    };
    const testid = "create-unassigned-task-modal";
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid={testid}>
            <div className="bg-white rounded-lg p-4 w-[440px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">New unassigned task</h3>
                <p className="text-[12px] text-[#666]">
                    Drafts live outside the workstage lifecycle until you assign them.
                </p>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Title
                    <input value={form.title}
                           onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-title`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Description
                    <textarea value={form.description} rows={3}
                              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                              className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                              data-testid={`${testid}-description`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Owner
                    <select value={form.owner_id}
                            onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                            className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                            data-testid={`${testid}-owner`}>
                        <option value="">Unassigned</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.display_name || a.email}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Due date
                    <input type="date" value={form.due_at}
                           onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-due`} />
                </label>
                <div className="flex gap-2 justify-end mt-1">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid={`${testid}-cancel`}>Cancel</button>
                    <button onClick={save} disabled={saving}
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-50"
                            data-testid={`${testid}-submit`}>
                        {saving ? "Saving…" : "Create draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EditUnassignedTaskModal({ draft, admins, onClose, onSaved }) {
    const toDateInput = (iso) => {
        if (!iso) return "";
        try { return new Date(iso).toISOString().slice(0, 10); }
        catch { return ""; }
    };
    const [form, setForm] = useState({
        title: draft?.title || "",
        description: draft?.description || "",
        owner_id: draft?.owner_id || "",
        due_at: toDateInput(draft?.due_at),
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/unassigned-tasks/${draft.id}`, {
                title: form.title,
                description: form.description || null,
                owner_id: form.owner_id || null,
                due_at: form.due_at || null,
            });
            toast.success("Draft updated");
            onClose();
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save draft");
        } finally {
            setSaving(false);
        }
    };
    const testid = "edit-unassigned-task-modal";
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid={testid}>
            <div className="bg-white rounded-lg p-4 w-[440px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">Edit unassigned task</h3>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Title
                    <input value={form.title}
                           onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-title`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Description
                    <textarea value={form.description} rows={3}
                              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                              className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                              data-testid={`${testid}-description`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Owner
                    <select value={form.owner_id}
                            onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                            className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                            data-testid={`${testid}-owner`}>
                        <option value="">Unassigned</option>
                        {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.display_name || a.email}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Due date
                    <input type="date" value={form.due_at}
                           onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-due`} />
                </label>
                <div className="flex gap-2 justify-end mt-1">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid={`${testid}-cancel`}>Cancel</button>
                    <button onClick={save} disabled={saving}
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-50"
                            data-testid={`${testid}-submit`}>
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DeleteUnassignedTaskModal({ draft, onClose, onDeleted }) {
    // Drafts pre-date the auditable record — a one-click confirmation
    // suffices; no mandatory reason.
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/unassigned-tasks/${draft.id}`);
            toast.success("Draft deleted");
            onClose();
            onDeleted();
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not delete draft");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid="delete-unassigned-task-modal">
            <div className="bg-white rounded-lg p-4 w-[400px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">Delete draft</h3>
                <p className="text-[13px] text-[#666]">
                    Delete <span className="font-semibold text-charcoal">"{draft.title}"</span>? This cannot be undone.
                </p>
                <div className="flex gap-2 justify-end">
                    <button onClick={onClose}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                            data-testid="delete-unassigned-task-cancel">Cancel</button>
                    <button onClick={submit} disabled={busy}
                            className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md disabled:opacity-50"
                            data-testid="delete-unassigned-task-confirm">
                        {busy ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}
